import { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { mindtimeWazuhComplianceApi } from 'src/api/mindtime/wazuhComplianceApi';
import { useUserProfile } from 'src/contexts/UserProfileContext';
import { useEdrDataGate } from 'src/mindtime/hooks/useEdrDataGate';
import { useSubscriptionModulePermissions } from 'src/mindtime/hooks/useSubscriptionPermissionAccess';
import { EDR_SOLUTION_ID } from 'src/mindtime/constants/securitySolutions';
import { guardEdrPreviewAction } from 'src/mindtime/utils/edrPreviewGuard';
import { useWazuhScope } from 'src/mindtime/hooks/useWazuhScope';
import type {
  WazuhComplianceControlItem,
  WazuhComplianceControlsQuery,
  WazuhComplianceEndpointItem,
  WazuhComplianceSummaryData,
} from 'src/types/mindtime/wazuhCompliance';
import type { WazuhPagedData, WazuhScopeParams } from 'src/types/mindtime/wazuhDashboard';
import logger from 'src/utils/logger';
import { buildEdrApiScope } from '../shared/wazuhScopeParams';

export type ComplianceFilters = {
  framework?: string;
  status?: string;
  category?: string;
  wazuhAgentId?: string;
  organizationId?: string;
};

export type ComplianceSortParams = {
  column: string;
  sort: 'asc' | 'desc';
};

export type ComplianceState = {
  loading: boolean;
  summary: WazuhComplianceSummaryData | null;
  controls: WazuhPagedData<WazuhComplianceControlItem> | null;
  endpoints: WazuhPagedData<WazuhComplianceEndpointItem> | null;
};

type BuildComplianceQueryInput = {
  scope: WazuhScopeParams;
  profileOrgId?: string;
  isReseller: boolean;
  search?: string;
  filters?: ComplianceFilters;
  sortParams?: ComplianceSortParams | null;
  page?: number;
  pageSize?: number;
};

const emptyState = (): ComplianceState => ({
  loading: true,
  summary: null,
  controls: null,
  endpoints: null,
});

const buildComplianceQuery = ({
  scope,
  profileOrgId,
  isReseller,
  search = '',
  filters = {},
  selectedEndpointId,
  sortParams,
  page,
  pageSize,
}: BuildComplianceQueryInput & { selectedEndpointId?: string | null }): WazuhComplianceControlsQuery => {
  const { framework, status, category, organizationId } = filters;
  const wazuhAgentId = selectedEndpointId?.trim() || filters.wazuhAgentId;
  const effectiveScope = buildEdrApiScope({
    scope,
    profileOrgId,
    isReseller,
    pageOrganizationId: organizationId || undefined,
  });

  return {
    ...effectiveScope,
    ...(typeof page === 'number' ? { page } : {}),
    ...(typeof pageSize === 'number' ? { pageSize } : {}),
    ...(search ? { search } : {}),
    ...(framework ? { framework } : {}),
    ...(status ? { status } : {}),
    ...(category ? { category } : {}),
    ...(wazuhAgentId ? { wazuhAgentId } : {}),
    ...(sortParams ? { column: sortParams.column, sort: sortParams.sort } : {}),
  };
};

const triggerBlobDownload = (blob: Blob, filename: string) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.parentNode?.removeChild(link);
  window.URL.revokeObjectURL(url);
};

export const useComplianceExport = (
  search = '',
  filters: ComplianceFilters = {},
  sortParams: ComplianceSortParams | null = null
) => {
  const { scope, isReseller, selectedEndpointId } = useWazuhScope();
  const { userData } = useUserProfile();
  const { isPreviewMode } = useEdrDataGate('configuration-compliance');
  const profileOrgId = userData?.organization?.id;
  const [exporting, setExporting] = useState(false);

  const handleExport = useCallback(() => {
    if (guardEdrPreviewAction(isPreviewMode, 'previewExportBlocked')) return;
    setExporting(true);
    mindtimeWazuhComplianceApi
      .export(
        buildComplianceQuery({
          scope,
          profileOrgId,
          isReseller,
          search,
          filters,
          sortParams,
          selectedEndpointId,
        })
      )
      .then(({ blob, filename }) => {
        const name = filename?.trim() ? filename : 'compliance-controls-export.csv';
        triggerBlobDownload(blob, name);
        toast.success('Export downloaded');
      })
      .catch((error) => {
        logger(error, 'error', 'useComplianceExport');
        toast.error(error instanceof Error ? error.message : 'Export failed');
      })
      .finally(() => {
        setExporting(false);
      });
  }, [scope, profileOrgId, isReseller, search, filters, sortParams, selectedEndpointId, isPreviewMode]);

  return { exporting, handleExport };
};

export const useComplianceData = (
  page = 0,
  pageSize = 50,
  search = '',
  filters: ComplianceFilters = {},
  sortParams: ComplianceSortParams | null = null
): ComplianceState => {
  const { scope, isReseller, selectedEndpointId } = useWazuhScope();
  const { userData } = useUserProfile();
  const { skipLive, wait, isPreviewMode, getPreviewState } = useEdrDataGate('configuration-compliance');
  const { canWidget } = useSubscriptionModulePermissions(EDR_SOLUTION_ID, 'configuration-compliance');
  const canSummary = canWidget('summary');
  const canList = canWidget('list');
  const canEndpointCompliance = canWidget('endpointCompliance');
  const profileOrgId = userData?.organization?.id;
  const [state, setState] = useState<ComplianceState>(emptyState);

  const { framework, status, category, organizationId } = filters;
  const wazuhAgentId = selectedEndpointId?.trim() || filters.wazuhAgentId || '';

  useEffect(() => {
    if (wait) return undefined;
    if (isPreviewMode) {
      setState(getPreviewState() as ComplianceState);
      return undefined;
    }

    if (!canSummary) {
      setState((prev) => ({ ...prev, summary: null }));
      return undefined;
    }

    let cancelled = false;
    const summaryParams = buildComplianceQuery({
      scope,
      profileOrgId,
      isReseller,
      filters,
      selectedEndpointId,
    });

    mindtimeWazuhComplianceApi
      .getSummary({
        resellerId: summaryParams.resellerId,
        organizationId: summaryParams.organizationId,
        framework: summaryParams.framework,
      })
      .then((summary) => {
        if (!cancelled) setState((prev) => ({ ...prev, summary }));
      })
      .catch((error) => {
        if (!cancelled) {
          logger(error, 'error', 'useComplianceData.summary');
          setState((prev) => ({ ...prev, summary: null }));
        }
      });

    return () => {
      cancelled = true;
    };
  }, [scope, profileOrgId, isReseller, organizationId, framework, isPreviewMode, wait, getPreviewState, canSummary]);

  useEffect(() => {
    if (skipLive) return undefined;
    if (!canList) {
      setState((prev) => ({ ...prev, loading: false, controls: null }));
      return undefined;
    }

    let cancelled = false;
    setState((prev) => ({ ...prev, loading: true }));

    const listParams = buildComplianceQuery({
      scope,
      profileOrgId,
      isReseller,
      search,
      filters,
      sortParams,
      page,
      pageSize,
      selectedEndpointId,
    });

    mindtimeWazuhComplianceApi
      .getControls(listParams)
      .then((controls) => {
        if (!cancelled) setState((prev) => ({ ...prev, loading: false, controls }));
      })
      .catch((error) => {
        if (!cancelled) {
          logger(error, 'error', 'useComplianceData.controls');
          setState((prev) => ({ ...prev, loading: false, controls: null }));
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    scope,
    profileOrgId,
    isReseller,
    page,
    pageSize,
    search,
    framework,
    status,
    category,
    wazuhAgentId,
    organizationId,
    sortParams?.column,
    sortParams?.sort,
    isPreviewMode,
    skipLive,
    canList,
  ]);

  useEffect(() => {
    if (skipLive) return undefined;
    if (!canEndpointCompliance) {
      setState((prev) => ({ ...prev, endpoints: null }));
      return undefined;
    }

    let cancelled = false;

    const listParams = buildComplianceQuery({
      scope,
      profileOrgId,
      isReseller,
      search,
      filters,
      sortParams,
      page: 0,
      pageSize,
      selectedEndpointId,
    });

    mindtimeWazuhComplianceApi
      .listEndpoints(listParams)
      .then((endpoints) => {
        if (!cancelled) setState((prev) => ({ ...prev, endpoints }));
      })
      .catch((error) => {
        if (!cancelled) {
          logger(error, 'error', 'useComplianceData.endpoints');
          setState((prev) => ({ ...prev, endpoints: null }));
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    scope,
    profileOrgId,
    isReseller,
    pageSize,
    search,
    framework,
    status,
    category,
    wazuhAgentId,
    organizationId,
    sortParams?.column,
    sortParams?.sort,
    isPreviewMode,
    skipLive,
    canEndpointCompliance,
  ]);

  return state;
};
